import styled from '@emotion/styled';
import type { HeroCustomizationAsset } from '../customization';

export interface HeroMediaProps {
  asset?: HeroCustomizationAsset;
  fallbackDesktopSrc?: string;
  fallbackMobileSrc?: string;
  alt?: string;
  autoPlay?: boolean;
  className?: string;
  decorativeVideo?: boolean;
  objectPosition?: string;
  onLoad?: () => void;
}

const HeroFrame = styled.div`
  width: 100%;
  height: 100%;
  min-height: inherit;

  picture {
    display: block;
    width: 100%;
    height: 100%;
  }
`;

const HeroImage = styled.img<{ $objectPosition: string }>`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: ${({ $objectPosition }) => $objectPosition};
`;

const HeroVideo = styled.video<{ $objectPosition: string }>`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: ${({ $objectPosition }) => $objectPosition};

  @media (prefers-reduced-motion: reduce) {
    display: none;
  }
`;

export function HeroMedia({
  asset,
  fallbackDesktopSrc,
  fallbackMobileSrc,
  alt = 'Hero banner',
  autoPlay = false,
  className,
  decorativeVideo = false,
  objectPosition = 'center top',
  onLoad,
}: HeroMediaProps) {
  const resolvedAlt = asset?.alt ?? alt;
  const assetSrc = asset?.src.trim();
  const assetMobileSrc = asset?.kind === 'image' ? asset.mobileSrc?.trim() : undefined;
  const assetPoster = asset?.kind === 'video' ? asset.poster?.trim() : undefined;

  if (asset?.kind === 'video' && assetSrc) {
    return (
      <HeroFrame className={className}>
        <HeroVideo
          src={assetSrc}
          poster={assetPoster}
          aria-hidden={decorativeVideo || undefined}
          aria-label={decorativeVideo ? undefined : resolvedAlt}
          role={decorativeVideo ? 'presentation' : undefined}
          autoPlay={autoPlay}
          controls
          muted
          loop={autoPlay}
          preload="metadata"
          playsInline
          $objectPosition={objectPosition}
          onLoadedData={onLoad}
        />
      </HeroFrame>
    );
  }

  const desktopSrc = (asset?.kind === 'image' ? assetSrc : undefined) || fallbackDesktopSrc;
  const mobileSrc = assetMobileSrc || fallbackMobileSrc || desktopSrc;

  if (!desktopSrc) {
    return null;
  }

  return (
    <HeroFrame className={className}>
      <picture>
        {mobileSrc && <source media="(max-width: 640px)" srcSet={mobileSrc} />}
        <source media="(min-width: 641px)" srcSet={desktopSrc} />
        <HeroImage src={desktopSrc} alt={resolvedAlt} loading="eager" $objectPosition={objectPosition} onLoad={onLoad} />
      </picture>
    </HeroFrame>
  );
}
